import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { TrendingUp, Shield, Users, Award, ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";

const Home = () => {
  const features = [
    {
      icon: TrendingUp,
      title: "Professional Portfolio Management",
      description: "Expert-managed model portfolios designed for optimal risk-adjusted returns."
    },
    {
      icon: Shield,
      title: "FCA Regulated",
      description: "Fully authorised and regulated by the Financial Conduct Authority for your peace of mind."
    },
    {
      icon: Users,
      title: "Tailored Solutions",
      description: "Personalised investment strategies that align with your financial goals and risk tolerance."
    },
    {
      icon: Award,
      title: "Award-Winning Service",
      description: "Our team members have recognised excellence in investment management and client service across the UK."
    }
  ];

  const portfolios = [
    {
      name: "Conservative Growth",
      risk: "Low",
      expectedReturn: "4-6%",
      description: "Ideal for cautious investors seeking steady growth with capital preservation."
    },
    {
      name: "Balanced Portfolio",
      risk: "Medium",
      expectedReturn: "6-8%",
      description: "Perfect balance of growth and stability for medium-term investment goals."
    },
    {
      name: "Growth Portfolio",
      risk: "Medium-High",
      expectedReturn: "8-12%",
      description: "Designed for investors comfortable with higher risk for greater potential returns."
    }
  ];

  return (
    <div>
      <SEO
        title="Cape Berkshire Asset Management — Model Portfolio Services"
        description="FCA-regulated discretionary investment manager delivering risk-targeted Model Portfolio Services and bespoke portfolios for UK financial advisers and their clients."
        path="/"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="/images/cbam-logo-updated.png" 
                alt="Cape Berkshire Asset Management" 
                className="h-60 md:h-72 w-auto mx-auto mb-4"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Professional Model Portfolio Services
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-foreground/90 max-w-3xl mx-auto">
              Expert investment management for UK investors. FCA regulated, professionally managed, 
              tailored to your financial goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/portfolios">
                  View Our Portfolios
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Cape Berkshire Asset Management?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We combine decades of investment expertise with cutting-edge technology to deliver 
              exceptional results for our clients.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center border-0 shadow-lg">
                <CardHeader>
                  <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* MPS Ranges Section */}
      <section className="py-20 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Model Portfolio Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Discover our comprehensive range of professionally managed portfolios, each designed 
              for different risk profiles and investment objectives.
            </p>
            <Button size="lg" asChild>
              <Link to="/portfolios">
                View MPS Ranges
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Investment Journey?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            Speak with our team of investment professionals to discuss your financial goals 
            and find the right portfolio for you.
          </p>
          <Button size="lg" variant="outline" className="border-primary-foreground text-primary bg-primary-foreground hover:bg-primary hover:text-primary-foreground" asChild>
            <Link to="/portfolios">View All Portfolios</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;